import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { Drawer, Field } from "../components/UI";
import { Page } from "../components/AppShell";
import { Plus, CalendarBlank, Trash, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

const TYPES = ["Meeting", "Court Appearance", "Service Attempt", "Deadline", "Task", "Other"];
const VIEWS = ["day", "week", "month"];

export default function Calendar() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "Meeting" });
  // Honour user.calendar_default_view; falls back to "week".
  const initialView = VIEWS.includes(user?.calendar_default_view) ? user.calendar_default_view : "week";
  const [view, setView] = useState(initialView);
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [err, setErr] = useState("");

  // If the user's pref loads in late (after first render), align once.
  useEffect(() => {
    if (user?.calendar_default_view && VIEWS.includes(user.calendar_default_view)) {
      setView(user.calendar_default_view);
    }
  }, [user?.calendar_default_view]);

  const load = () => {
    api.get("/calendar").then(r => setItems(r.data));
    api.get("/calendar/statuses").then(r => setStatuses(r.data));
    api.get("/jobs").then(r => setJobs(r.data));
    api.get("/users").then(r => setUsers(r.data));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/calendar", {
        type: form.type,
        details: form.details || null,
        start_time: new Date(form.start_time).toISOString(),
        end_time: form.end_time ? new Date(form.end_time).toISOString() : null,
        event_length: form.event_length ? parseInt(form.event_length) : null,
        task_deadline: form.task_deadline ? new Date(form.task_deadline).toISOString() : null,
        job_id: form.job_id || null,
        calendar_item_status_id: form.calendar_item_status_id || null,
        assigned_user_id: form.assigned_user_id || null,
      });
      setOpen(false); setForm({ type: "Meeting" }); load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this calendar item?")) return;
    await api.delete(`/calendar/${id}`); load();
  };

  // ---- view-specific date math ----
  const range = useMemo(() => {
    if (view === "day")   return [startOfDay(anchor)];
    if (view === "week")  return Array.from({ length: 7 }).map((_, i) => addDays(startOfWeek(anchor), i));
    // month: grid that starts on the Sunday of the week containing the 1st
    const first = startOfMonth(anchor);
    const gridStart = startOfWeek(first);
    return Array.from({ length: 42 }).map((_, i) => addDays(gridStart, i));
  }, [view, anchor]);

  const itemsByDay = useMemo(() => {
    const map = {};
    items.forEach(it => {
      const key = new Date(it.start_time).toDateString();
      (map[key] = map[key] || []).push(it);
    });
    Object.values(map).forEach(arr => arr.sort((a, b) => new Date(a.start_time) - new Date(b.start_time)));
    return map;
  }, [items]);

  const headerLabel = useMemo(() => {
    if (view === "day")  return anchor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    if (view === "week") {
      const s = startOfWeek(anchor); const e = addDays(s, 6);
      return `${s.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${e.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
    }
    return anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }, [view, anchor]);

  const nudge = (dir) => {
    if (view === "day")   setAnchor(addDays(anchor,  dir));
    if (view === "week")  setAnchor(addDays(anchor,  dir * 7));
    if (view === "month") {
      const d = new Date(anchor); d.setMonth(d.getMonth() + dir); setAnchor(d);
    }
  };
  const goToday = () => setAnchor(startOfDay(new Date()));

  return (
    <Page
      title="Calendar"
      subtitle={`${view.toUpperCase()} VIEW`}
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "Calendar"]}
      testid="calendar-page"
      actions={
        <div className="flex gap-2 items-center">
          <div className="flex gap-1 mr-2" data-testid="calendar-view-toggle">
            {VIEWS.map(v => (
              <button
                key={v}
                className={`btn btn-sm ${v === view ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setView(v)}
                data-testid={`calendar-view-${v}`}
              >{v.toUpperCase()}</button>
            ))}
          </div>
          <button className="btn btn-ghost" onClick={() => nudge(-1)} data-testid="prev-btn"><CaretLeft size={14} /></button>
          <button className="btn btn-ghost" onClick={goToday} data-testid="today-btn">TODAY</button>
          <button className="btn btn-ghost" onClick={() => nudge(1)} data-testid="next-btn"><CaretRight size={14} /></button>
          <button className="btn btn-primary" onClick={() => { setForm({ type: "Meeting", start_time: toLocalInput(new Date()) }); setErr(""); setOpen(true); }} data-testid="new-calendar-btn"><Plus size={14} /> NEW</button>
        </div>
      }
    >

      <div className="text-sm text-muted-foreground mb-3 mono uppercase tracking-widest" data-testid="calendar-header-label">
        {headerLabel}
      </div>

      {view === "day"   && <DayView   day={anchor} list={itemsByDay[anchor.toDateString()] || []} onRemove={remove} />}
      {view === "week"  && <WeekView  days={range} itemsByDay={itemsByDay} onRemove={remove} />}
      {view === "month" && <MonthView anchor={anchor} days={range} itemsByDay={itemsByDay} setAnchor={setAnchor} setView={setView} />}

      {items.length === 0 && (
        <div className="surface mt-4 p-12 text-center text-muted-foreground" data-testid="empty-calendar">
          <CalendarBlank size={32} weight="thin" className="mx-auto mb-3" />
          No calendar items yet. Add one to populate this view.
        </div>
      )}

      <Drawer open={open} onClose={() => setOpen(false)} title="New calendar item" size="narrow" testid="new-calendar-drawer"
        footer={<><button className="btn btn-secondary" onClick={() => setOpen(false)}>CANCEL</button><button className="btn btn-primary" onClick={submit} disabled={!form.start_time} data-testid="save-cal-btn"><Plus size={14} /> CREATE</button></>}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="TYPE">
            <select className="input" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} data-testid="cal-type-select">
              {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="STATUS">
            <select className="input" value={form.calendar_item_status_id || ""} onChange={e => setForm({ ...form, calendar_item_status_id: e.target.value })}>
              <option value="">—</option>
              {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="START"><input type="datetime-local" className="input" value={form.start_time || ""} onChange={e => setForm({ ...form, start_time: e.target.value })} data-testid="cal-start-input" /></Field>
          <Field label="END"><input type="datetime-local" className="input" value={form.end_time || ""} onChange={e => setForm({ ...form, end_time: e.target.value })} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="LINK TO JOB">
            <select className="input" value={form.job_id || ""} onChange={e => setForm({ ...form, job_id: e.target.value })}>
              <option value="">—</option>
              {jobs.map(j => <option key={j.id} value={j.id}>{j.job_number || j.id.slice(0, 8)}</option>)}
            </select>
          </Field>
          <Field label="ASSIGNED">
            <select className="input" value={form.assigned_user_id || ""} onChange={e => setForm({ ...form, assigned_user_id: e.target.value })}>
              <option value="">—</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
        </div>
        <Field label="DETAILS"><textarea className="input" rows={3} value={form.details || ""} onChange={e => setForm({ ...form, details: e.target.value })} /></Field>
        {err && <div className="text-xs text-destructive mt-2">{err}</div>}
      </Drawer>
    </Page>
  );
}

// ---------------- views ----------------

function DayView({ day, list, onRemove }) {
  // 24 hour rows, items overlaid by their start hour. Simple list view —
  // good enough for now; can become a timeline later.
  const isToday = day.toDateString() === new Date().toDateString();
  return (
    <div className={`surface ${isToday ? "border-2 border-primary" : ""}`} data-testid={`day-view-${day.toISOString().slice(0, 10)}`}>
      {list.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">—</div>
      ) : (
        <div className="divide-y divide-border">
          {list.map(it => (
            <CalRow key={it.id} it={it} onRemove={onRemove} />
          ))}
        </div>
      )}
    </div>
  );
}

function WeekView({ days, itemsByDay, onRemove }) {
  return (
    <div className="grid grid-cols-7 gap-2" data-testid="week-grid">
      {days.map((d, i) => {
        const isToday = d.toDateString() === new Date().toDateString();
        const list = itemsByDay[d.toDateString()] || [];
        return (
          <div key={i} className={`surface min-h-[180px] p-2 ${isToday ? "border-2 border-primary" : ""}`} data-testid={`day-${d.toISOString().slice(0, 10)}`}>
            <div className="flex items-baseline justify-between mb-2">
              <div className="label-xs">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
              <div className="text-lg mono">{d.getDate()}</div>
            </div>
            <div className="space-y-1">
              {list.length === 0
                ? <div className="text-xs text-muted-foreground">—</div>
                : list.map(it => (
                  <div key={it.id} className="text-xs bg-secondary/40 p-1.5 rounded-sm group flex items-start justify-between gap-1" data-testid={`cal-item-${it.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{it.type}</div>
                      <div className="text-muted-foreground">{new Date(it.start_time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</div>
                      {it.details && <div className="text-muted-foreground truncate">{it.details}</div>}
                    </div>
                    <button onClick={() => onRemove(it.id)} className="text-destructive opacity-0 group-hover:opacity-100" data-testid={`delete-cal-${it.id}`}><Trash size={10} /></button>
                  </div>
                ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthView({ anchor, days, itemsByDay, setAnchor, setView }) {
  // 6-row × 7-col grid. Cells outside the focused month dim slightly.
  // Clicking a cell drills into Day view for that date.
  const focusedMonth = anchor.getMonth();
  return (
    <div data-testid="month-grid">
      <div className="grid grid-cols-7 gap-1 mb-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
          <div key={d} className="label-xs px-2">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          const isToday = d.toDateString() === new Date().toDateString();
          const inMonth = d.getMonth() === focusedMonth;
          const list = itemsByDay[d.toDateString()] || [];
          return (
            <button
              key={i}
              onClick={() => { setAnchor(startOfDay(d)); setView("day"); }}
              className={`surface min-h-[96px] p-1.5 text-left transition-colors hover:bg-muted/30 ${isToday ? "border-2 border-primary" : ""} ${inMonth ? "" : "opacity-40"}`}
              data-testid={`month-cell-${d.toISOString().slice(0, 10)}`}
            >
              <div className="text-xs mono text-right">{d.getDate()}</div>
              <div className="space-y-0.5 mt-1">
                {list.slice(0, 3).map(it => (
                  <div key={it.id} className="text-[10px] truncate bg-secondary/40 px-1 rounded-sm" title={`${it.type} — ${new Date(it.start_time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`}>
                    {new Date(it.start_time).toLocaleTimeString(undefined, { hour: "numeric" })} {it.type}
                  </div>
                ))}
                {list.length > 3 && (
                  <div className="text-[10px] text-muted-foreground">+{list.length - 3} more</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CalRow({ it, onRemove }) {
  return (
    <div className="flex items-start justify-between gap-3 p-3 group" data-testid={`cal-item-${it.id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-xs mono text-muted-foreground tracking-widest uppercase">
          {new Date(it.start_time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
          {it.end_time && ` – ${new Date(it.end_time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`}
        </div>
        <div className="font-semibold">{it.type}</div>
        {it.details && <div className="text-sm text-muted-foreground">{it.details}</div>}
        {it.job_id && <Link to={`/job-dash/${it.job_id}`} className="text-xs text-blue-600 hover:underline">→ open job</Link>}
      </div>
      <button onClick={() => onRemove(it.id)} className="text-destructive opacity-0 group-hover:opacity-100 btn btn-ghost btn-sm" data-testid={`delete-cal-${it.id}`}><Trash size={12} /></button>
    </div>
  );
}

// ---------------- helpers ----------------

function startOfDay(d) { const dt = new Date(d); dt.setHours(0, 0, 0, 0); return dt; }
function startOfWeek(d) {
  const dt = new Date(d); const day = dt.getDay(); dt.setHours(0, 0, 0, 0);
  dt.setDate(dt.getDate() - day); return dt;
}
function startOfMonth(d) { const dt = new Date(d); dt.setDate(1); dt.setHours(0, 0, 0, 0); return dt; }
function addDays(d, n) { const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt; }
function toLocalInput(d) {
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
